import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { graphql, useLazyLoadQuery } from "react-relay"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import {
  countrySearchQuery,
  countrySearchQuery$data,
} from "@/modules/common/address-section/__generated__/countrySearchQuery.graphql"
import { AddressSearch, CommonSearchProps, OptionItem } from "@/modules/common/address-section/address-search"

type Props = CommonSearchProps

type Response = countrySearchQuery$data["displayableCountries"]

const HIGHLIGHT_COUNTRIES = ["CH", "DE", "FR", "IT"]

const CountrySearch = (rest: Props) => {
  const [keyword, setKeyword] = useState("")
  const filterRef = useRef(false)
  const language = useCurrentLanguage()

  const { displayableCountries: countries } = useLazyLoadQuery<countrySearchQuery>(
    graphql`
      query countrySearchQuery($language: String!) {
        displayableCountries(language: $language) {
          id
          countryName
          language
          countryLookup {
            countryIsoCode2
          }
        }
      }
    `,
    { language },
  )

  const filterCountries = useCallback(
    (cities: Response) => {
      if (filterRef.current) {
        return (
          cities?.filter(
            (item) =>
              item.countryName.toLowerCase().includes(keyword.toLowerCase()) &&
              language.toUpperCase() === item.language,
          ) || []
        )
      }
      return cities?.filter((item) => language.toUpperCase() === item.language) || []
    },
    [keyword, language],
  )
  const countryOptions = useMemo(() => {
    const activeCountry = filterCountries(countries)
    const filtered = activeCountry.map((item) => ({
      id: item.id,
      label: item.countryName,
      value: item.countryName,
      code: item.countryLookup.countryIsoCode2 || "",
      order: HIGHLIGHT_COUNTRIES.includes(item.countryLookup.countryIsoCode2) ? `1` : `0`,
    }))

    const options = filtered.filter((item) => !HIGHLIGHT_COUNTRIES.includes(item.code))

    const highlight = filtered.filter((item) => HIGHLIGHT_COUNTRIES.includes(item.code))

    return { options, highlight }
  }, [countries, filterCountries])

  // when the field has an initial values
  useEffect(() => {
    const currentCountry = [...countryOptions.options, ...countryOptions.highlight].find(
      (country) => country.value === rest.value || country.code === rest.value,
    )
    if (currentCountry) {
      setKeyword(currentCountry.label)
      rest?.onSelect({
        country: currentCountry.code,
        value: currentCountry.code || "",
        label: currentCountry.label,
        id: currentCountry.id,
      })
    }
  }, [countryOptions, rest, keyword])

  const handleSelection = (option: OptionItem) => {
    const currentCountry = [...countryOptions.options, ...countryOptions.highlight].find(
      (country) => country.value === option.value,
    )

    if (currentCountry) {
      rest.onSelect({
        // we need to override value to country code as it is needed for country creation
        country: currentCountry.code,
        value: currentCountry.code,
        label: currentCountry.label,
        id: currentCountry.id,
      })
    }
  }

  const handleSearchCountry = (value: string) => {
    filterRef.current = true
    setKeyword(value)
  }

  return (
    <AddressSearch
      type="country"
      className="col-span-4"
      highlightOptions={countryOptions.highlight}
      options={countryOptions.options}
      placeholder={rest.placeholder}
      error={rest.error}
      onClear={rest.onClear}
      value={keyword}
      onSelect={handleSelection}
      setKeyword={(value) => handleSearchCountry(value)}
    />
  )
}

export { CountrySearch }
